import { FC, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { ThumbnailModalProps } from "../View/_CardGroup";
import Api from "Vendor/Api";
import { useListingContext } from "Hooks/useListingContext";
import { useKeyboardShortcut } from "Hooks/useKeyboardShortcut";
import useTranslationDataUtils from "Vendor/Utils/TranslationDataUtils";

export interface _PreviewFileProps {
    thumbnailModalProps?: ThumbnailModalProps
    setThumbnailModalProps: (modalProps?: ThumbnailModalProps) => void
}

const _PreviewFile: FC<_PreviewFileProps> = ({
    thumbnailModalProps,
    setThumbnailModalProps,
}) => {
    const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined)
    const [thumbnailType, setThumbnailType] = useState<string | undefined>(undefined)
    const [isLoaded, setIsLoaded] = useState<boolean>(true)
    const { translate } = useTranslationDataUtils()

    const { items, currentPage, setCurrentPage, pages } = useListingContext()

    const itemsArray = Object.values(items)
    const currentIndex = itemsArray.findIndex(item => item.id === thumbnailModalProps?.id)

    const prevItem = currentIndex > 0 ? itemsArray[currentIndex - 1] : null
    const nextItem = currentIndex < itemsArray.length - 1 ? itemsArray[currentIndex + 1] : null

    const createThumbnailModalProps = (item: any) => {
        if (!item) return null

        return {
            path: item.view || item.thumbnail || '',
            name: item.name || translate(item, 'name'),
            mimeType: item.viewMimeType || item.mimeType || '',
            id: item.id ?? null,
        }
    }

    const isLeftEnabled = (isLoaded && prevItem) || pages.includes(currentPage - 1)
    const isRightEnabled = (isLoaded && nextItem) || pages.includes(currentPage + 1)

    const handleRightClick = () => {
        if (nextItem) {
            const nextItemProps = createThumbnailModalProps(nextItem)
            if (nextItemProps) {
                setThumbnailModalProps(nextItemProps)
            }
        } else {
            goToNextPage()
        }
    }

    const handleLeftClick = () => {
        if (prevItem) {
            const prevItemProps = createThumbnailModalProps(prevItem)
            if (prevItemProps) {
                setThumbnailModalProps(prevItemProps)
            }
        } else {
            goToPreviousPage()
        }
    }

    const goToNextPage = () => {
        if (pageExists(currentPage + 1)) {
            onExited()
            setCurrentPage(currentPage + 1)
        }
    }

    const goToPreviousPage = () => {
        if (pageExists(currentPage - 1)) {
            onExited()
            setCurrentPage(currentPage - 1)
        }
    }

    const pageExists = (page: number) => {
        return pages.includes(page)
    }

    const onExited = () => {
        setThumbnailModalProps(undefined)
    }

    useKeyboardShortcut({
        key: "ArrowLeft",
        onKeyPressed: () => handleLeftClick(),
        isEnabled: isLeftEnabled,
    })

    useKeyboardShortcut({
        key: "ArrowRight",
        onKeyPressed: () => handleRightClick(),
        isEnabled: isRightEnabled,
    })

    useEffect(() => {
        let tempThumbnailType = undefined
        if (thumbnailModalProps && thumbnailModalProps.mimeType) {
            if (thumbnailModalProps?.mimeType.includes("image")) {
                tempThumbnailType = "image"
            } else if (thumbnailModalProps?.mimeType.includes("video")) {
                tempThumbnailType = "video"
            } else if (thumbnailModalProps?.mimeType.includes("pdf")) {
                tempThumbnailType = "pdf"
            } else if (thumbnailModalProps?.mimeType.includes("audio")) {
                tempThumbnailType = "audio"
            } else if (thumbnailModalProps?.mimeType.includes("document")) {
                tempThumbnailType = "document"
                setIsLoaded(false)

                Api.get(`${thumbnailModalProps.path}/generate-unique-token`).then((response) => {
                    setPreviewUrl(response.data.url)
                    setIsLoaded(true)
                }).catch((error) => {
                    console.error("Error loading document preview:", error)
                    setIsLoaded(false)
                })
            }
        }
        setThumbnailType(tempThumbnailType)
    }, [thumbnailModalProps])

    return <>
        <div className="controls">
            <Button
                onClick={handleLeftClick}
                className="left-button"
                disabled={!isLeftEnabled}
            >
                <i className="fa-solid fa-chevron-left"></i>
            </Button>
            <Button
                onClick={handleRightClick}
                className="right-button"
                disabled={!isRightEnabled}
            >
                <i className="fa-solid fa-chevron-right"></i>
            </Button>
        </div>
        <div className="content">
            {
                thumbnailType && {
                    'image': <img src={thumbnailModalProps && thumbnailModalProps.path} alt={thumbnailModalProps && thumbnailModalProps.name} />,
                    'pdf': <iframe src={thumbnailModalProps && thumbnailModalProps.path} width="100%" height="631.25px"></iframe>,
                    'video': <video controls width="100%">
                        <source src={thumbnailModalProps && thumbnailModalProps.path} type={(thumbnailModalProps?.mimeType && thumbnailModalProps?.mimeType !== "video/quicktime") ? thumbnailModalProps?.mimeType : "video/mp4"} />
                    </video>,
                    'document': previewUrl
                        ? <iframe src={"https://view.officeapps.live.com/op/embed.aspx?src=".concat(encodeURIComponent(previewUrl))} style={{ border: 0 }} width="100%" height="631.25px"></iframe>
                        : <p>Preview on loading...</p>,
                    'audio': <audio controls>
                        <source src={thumbnailModalProps && thumbnailModalProps.path} type={thumbnailModalProps?.mimeType || "audio/mpeg"} />
                    </audio>,
                }[thumbnailType]
            }
        </div>
    </>
}

export default _PreviewFile
